import React from "react";
import { Button, Menu } from "@theorgchartdev/storybook";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { FormatFieldValue, appendParenthesizedValue } from "@coreapp/utils/FormatFieldValue";
import {
  GetSuperIDName,
  GetPositionSearchLabel
} from "@/appComponents/Blocks/Modals/RecordEditorModal/RecordEditor.utils";
import {
  enumFieldType,
  kParentPositionID,
  kPositionStartDate,
  kPositionEndDate
} from "@coreapp/constants";
import { IsAppendTableContext } from "@coreapp/utils/IsAppendTableContext";

export const usePositionRecall = ({
  dbObj,
  manager,
  baseRecordSupervisor,
  workingOverride,
  isMultiEdit,
  IsOnlyCommentChange,
  GetExistingFieldChanges,
  updateRecordEditorState,
  DoRevertAll,
  handleClose,
  wsMgr,
  setMadeFieldReverts,
  GetBaseFieldValue,
  GetOverrideStopIndex,
  LoadSupervisorDetails,
  updateRecordSupervisorAsDict,
  GetActiveChangeValueByField,
  changeResults
}) => {
  // DateInputField hard-codes its own postfixNode for the calendar picker, so a revert button
  // placed there is discarded and the field appears to have no revert option. Key off the field
  // type — the same signal FieldInputValuePickerComposite uses to pick DateInputField — because
  // GetFieldID() returns the raw stored ID, which GetFieldDefByID normalizes on lookup and so
  // does not reliably equal the kPosition*Date constants.
  const IsDateField = React.useCallback(fieldDef => {
    if (!fieldDef) return false;
    if (fieldDef.GetFieldType?.() === enumFieldType.date) return true;
    const fieldID = fieldDef.GetFieldID?.();
    return fieldID === kPositionStartDate || fieldID === kPositionEndDate;
  }, []);

  const FindFieldDef = React.useCallback(
    fieldID => {
      if (!dbObj) return null;
      const objClass = dbObj.GetParentClass();
      let fieldDef = objClass.GetFieldDefByID(fieldID);
      if (!fieldDef) fieldDef = objClass.GetFieldDefByName(fieldID);
      return fieldDef;
    },
    [dbObj]
  );

  const CanRevertField = React.useCallback(
    fieldDef => {
      if (isMultiEdit) return false;
      if (IsOnlyCommentChange()) return false;
      return true;
    },
    [isMultiEdit, IsOnlyCommentChange]
  );

  const ShowFieldHistory = React.useCallback(
    fieldDef => {
      if (!CanRevertField(fieldDef)) return false;
      const fieldChanges = GetExistingFieldChanges(fieldDef?.GetFieldName?.()) ?? [];
      return fieldChanges.length > 0;
    },
    [CanRevertField, GetExistingFieldChanges]
  );

  const ShowSingleRevertAll = React.useCallback(
    fieldDef => {
      const canRevert = ShowFieldHistory(fieldDef);
      const fieldChanges = GetExistingFieldChanges(fieldDef?.GetFieldName?.()) ?? [];
      return canRevert && fieldChanges.length <= 1;
    },
    [ShowFieldHistory, GetExistingFieldChanges]
  );

  const RevertFieldToSource = React.useCallback(
    (fieldDef, alert = true) => {
      const fieldName = fieldDef?.GetFieldName();
      if (!fieldName) return;

      if (alert) {
        wsMgr.CreateReactAlert(
          appstr("Revert all changes to") + " '" + fieldName + "'?",
          appstr("Confirm field revert"),
          () => {
            RevertFieldToSource(fieldDef, false);
          },
          () => {},
          appstr("Revert"),
          appstr("Cancel")
        );
        return;
      }

      // See RecordEditorModal.RevertFieldToSource: DoRevertAll is a whole-override delete
      // for the append table, so only the plan editor may take this shortcut. On position
      // charts GetSuperIDName resolves to the assigned-position field, which is why this
      // guard matters here too.
      if (fieldName === GetSuperIDName(dbObj) && !IsAppendTableContext(manager)) {
        DoRevertAll();
        handleClose();
        return;
      }

      if (workingOverride?.fChanges) {
        const stopIndex = GetOverrideStopIndex();
        for (let j = workingOverride.fChanges.length - 1; j >= stopIndex; j--) {
          const changeDelta = workingOverride.fChanges[j]?.fDelta;
          if (!changeDelta) continue;
          const changedFields = Object.keys(changeDelta);
          if (changedFields.length === 1 && changedFields[0] === fieldName) {
            workingOverride.fChanges.splice(j, 1);
          } else {
            delete workingOverride.fChanges[j].fDelta[fieldName];
          }
        }
      }

      // Drop any staged (changeResults) value for the reverted field, or it would still win
      // in GetCurrentFieldValue and be persisted by SaveRecordChanges. Mirrors
      // RecordEditorModal.RevertFieldToSource.
      const stateUpdate = { override: workingOverride };
      if (changeResults) {
        const { [fieldName]: _removed, ...remainingChangeResults } = changeResults;
        stateUpdate.changeResults = remainingChangeResults;
      }
      updateRecordEditorState(stateUpdate);
      if (setMadeFieldReverts) {
        setMadeFieldReverts(true);
      }

      // The Reports to chip renders from recordSupervisor state rather than the delta, so
      // stripping the delta alone leaves it showing the overridden value. See the matching
      // block in RecordEditorModal.RevertFieldToSource.
      if (fieldName === GetSuperIDName(dbObj)) {
        const revertedSuperID = GetActiveChangeValueByField?.(fieldName);
        if (revertedSuperID) LoadSupervisorDetails?.(workingOverride, revertedSuperID);
        else updateRecordSupervisorAsDict?.(null);
      }
    },
    [
      wsMgr,
      dbObj,
      manager,
      DoRevertAll,
      handleClose,
      workingOverride,
      updateRecordEditorState,
      setMadeFieldReverts,
      LoadSupervisorDetails,
      updateRecordSupervisorAsDict,
      GetActiveChangeValueByField,
      changeResults
    ]
  );

  const RevertFieldToPrior = React.useCallback(
    o => {
      const fieldName = o.field;
      const changeIdx = o.value;

      if (workingOverride?.fChanges && changeIdx >= 0) {
        let activeChangeIndex = -1;
        for (let i = workingOverride.fChanges.length - 1; i >= 0; i--) {
          if (workingOverride.fChanges[i].fDelta[fieldName] !== undefined) {
            activeChangeIndex = i;
            break;
          }
        }

        if (activeChangeIndex >= 0) {
          workingOverride.fChanges[activeChangeIndex].fDelta[fieldName] =
            workingOverride.fChanges[changeIdx].fDelta[fieldName];
        }

        updateRecordEditorState({ override: workingOverride });
        if (setMadeFieldReverts) {
          setMadeFieldReverts(true);
        }
      }
    },
    [workingOverride, updateRecordEditorState, setMadeFieldReverts]
  );

  const BuildPriorChangeValueOptions = React.useCallback(
    fieldName => {
      const options = [];
      const changeList = workingOverride.fChanges;
      let skipFirst = false;
      const fieldDef = FindFieldDef(fieldName);
      const stopIndex = GetOverrideStopIndex();

      for (let index = changeList.length - 1; index >= stopIndex; index--) {
        const nextChange = changeList[index];
        if (Object.keys(nextChange.fDelta).includes(fieldName)) {
          if (!skipFirst) {
            skipFirst = true;
            continue;
          }
          const nextValue = nextChange.fDelta[fieldName];
          const nextValue_formatted = FormatFieldValue(nextValue, fieldDef);

          const option = {
            value: index,
            label: nextValue_formatted || appstr("Blank"),
            clickHandler: RevertFieldToPrior,
            field: fieldName,
            divider: index === stopIndex
          };
          options.push(option);
        }
      }

      const rawBaseValue = GetBaseFieldValue(fieldDef);
      const formattedValue = FormatFieldValue(rawBaseValue, fieldDef) ?? appstr("Blank");

      const revertToSource = {
        clickHandler: () => RevertFieldToSource(fieldDef),
        revertAll: true,
        label: appendParenthesizedValue(appstr("Revert to source"), formattedValue)
      };
      options.push(revertToSource);

      return options;
    },
    [workingOverride, FindFieldDef, GetBaseFieldValue, RevertFieldToPrior, RevertFieldToSource]
  );

  const GetRevertButton = React.useCallback(
    fieldDef => {
      if (!ShowFieldHistory(fieldDef)) return null;

      let originalValue = GetBaseFieldValue(fieldDef);
      let formattedValue;

      if (fieldDef.GetFieldID() === kParentPositionID && baseRecordSupervisor) {
        formattedValue = GetPositionSearchLabel(baseRecordSupervisor);
      } else if (originalValue == null || originalValue === "") {
        formattedValue = appstr("Blank");
      } else {
        formattedValue = FormatFieldValue(originalValue, fieldDef);
      }

      const showSingleClickRevert = ShowSingleRevertAll(fieldDef);
      const planTip = appendParenthesizedValue(appstr("Revert to source"), formattedValue);
      const buttonId = showSingleClickRevert ? "position-single-revert-" : "position-multi-revert-";

      const trigger = (
        <Button
          dataTestId={buttonId + fieldDef.GetFieldID()}
          modifier={"tertiary"}
          iconOnly
          size="small"
          iconProps={{ name: showSingleClickRevert ? "rotate-left" : "clock-rotate-left" }}
          tooltipProps={{
            message: showSingleClickRevert ? planTip : appstr("Revert to prior value")
          }}
          onClick={showSingleClickRevert ? () => RevertFieldToSource(fieldDef) : undefined}
        />
      );

      if (showSingleClickRevert) return trigger;

      return (
        <Menu
          trigger={trigger}
          options={BuildPriorChangeValueOptions(fieldDef.GetFieldName())}
          listHeader={{ label: appstr("Field change history") }}
          value={"none"}
          listWidth={"medium"}
          optionSize={"small"}
          showCheck={false}
        />
      );
    },
    [
      ShowFieldHistory,
      GetBaseFieldValue,
      baseRecordSupervisor,
      ShowSingleRevertAll,
      RevertFieldToSource,
      BuildPriorChangeValueOptions
    ]
  );

  return {
    GetRevertButton,
    IsDateField
  };
};
